/* eslint-disable @typescript-eslint/no-explicit-any */

import { Layer } from './types';

// === ТИПЫ МЕТАБУЛЛИТОВ ===

// Типы текстовых сущностей
export type TextEntityType =
  | 'title'
  | 'subtitle'
  | 'heading-1'
  | 'heading-2'
  | 'heading-3'
  | 'bullet-level-1'
  | 'bullet-level-2'
  | 'bullet-level-3'
  | 'bullet-level-4'
  | 'digital-accent'
  | 'digital-accent-superscript';

/**
 * Опции для создания TextEntity объекта
 * Расширяет базовые опции Fabric.js с кастомными полями метабуллитов
 */
export interface TextEntityOptions {
  // Основной идентификатор
  id?: string;

  // Тип текстовой сущности
  entityType?: TextEntityType;

  // Layer для правильной работы с canvas
  layer?: Layer;

  // Уникальный ID метабуллита (группирует связанные сущности)
  metaBulletId?: string;

  // Порядковый номер в рамках метабуллита
  entityOrder?: number;

  // Уровень вложенности буллита (1-4, только для bullet-level-*)
  bulletLevel?: number;

  // Флаг что это сущность метабуллита
  isMetaBulletEntity?: boolean;

  // Дополнительные отступы
  marginBottom?: number;
  marginLeft?: number;

  // Исключить из бэкапа
  excludeFromBackup?: boolean;

  // === ПЕРСОНАЛЬНЫЕ НАСТРОЙКИ МАРКЕРА ===
  // Персональное содержимое маркера (переопределяет дефолтное из стилей)
  markerContent?: string;
  // Персональный тип маркера (переопределяет дефолтный из стилей)
  markerType?: MarkerType;

  // === ПОЛЯ ДЛЯ DIGITAL-ACCENT ===
  // ID группы для связывания digit и степени
  accentGroupId?: string;
  // Роль в группе digital-accent
  accentRole?: 'digit' | 'superscript' | 'subscript';
  // ID связанной сущности (digit <-> superscript)
  linkedAccentEntityId?: string;

  // Персональные стили для digital-accent entities
  personalDigitStyles?: Partial<
    TextEntityStyleConfig & { spacingTop?: number; spacingBottom?: number }
  >;
  personalSuperscriptStyles?: Partial<
    TextEntityStyleConfig & { offsetX?: number; offsetY?: number }
  >;

  // Внешняя конфигурация стилей (опционально)
  stylesConfig?: Partial<MetaBulletStylesConfig>;

  // Базовые опции Fabric.js
  left?: number;
  top?: number;
  width?: number;
  height?: number;
  originX?: 'left' | 'center' | 'right'; // Точка origin по X
  originY?: 'top' | 'center' | 'bottom'; // Точка origin по Y
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: string | number;
  fill?: string;
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  lineHeight?: number;
  visible?: boolean;
  selectable?: boolean;
  evented?: boolean;
  opacity?: number;
  angle?: number;
  scaleX?: number;
  scaleY?: number;
}

// === КОНФИГУРАЦИЯ СТИЛЕЙ ===

// Дефолтные стили для типов сущностей
export interface TextEntityStyleConfig {
  fontSize: number;
  fontFamily: string;
  fontWeight: string | number;
  lineHeight: number;
  // ⚠️ DEPRECATED: Поля marginBottom и marginTop устарели - используйте коэффициенты
  marginBottom?: number; // Оставлено для обратной совместимости
  marginTop?: number; // Оставлено для обратной совместимости
  marginLeft: number;
  textAlign: string;
  fill: string; // цвет текста

  // ✅ НОВАЯ СИСТЕМА: Коэффициенты отступов
  // Формула: (fontSize * lineHeight / 2) * коэффициент
  spacingTop?: number; // Коэффициент отступа сверху (по умолчанию 0)
  spacingBottom?: number; // Коэффициент отступа снизу (по умолчанию 0)
}

// === МАРКЕРЫ ===

// Типы маркеров
export type MarkerType = 'image' | 'svg' | 'text' | 'emoji';

// Позиция маркера относительно текста
export interface MarkerPosition {
  offsetX: number; // ✅ v7.0+: Gap между маркером и текстом (положительное значение 4-8px). ⚠️ DEPRECATED: отрицательные значения
  offsetY: number | 'top' | 'center' | 'bottom' | 'centered-by-first-line'; // Вертикальное смещение (число) или выравнивание относительно текстовой коробки
  width: number; // Ширина коробки маркера
  height: number; // Высота коробки маркера
}

// Базовая конфигурация маркера (только позиция и стили, без контента)
export interface MarkerLayoutConfig {
  enabled: boolean; // Включены ли маркеры для этого типа
  type?: MarkerType; // Дефолтный тип маркера (может быть переопределен в сущности)
  position: MarkerPosition; // Позиционирование
  style?: {
    // Дополнительные стили (для SVG/текста)
    fill?: string;
    stroke?: string;
    strokeWidth?: number;
    opacity?: number;
  };
}

// Полная конфигурация маркера с контентом (для обратной совместимости)
export interface MarkerConfig extends MarkerLayoutConfig {
  content: string; // Содержимое (URL изображения, SVG код, текст или эмодзи)
}

// Опции для создания объекта маркера
export interface MarkerOptions {
  id?: string;
  entityId: string; // ID связанной текстовой сущности
  entityType: TextEntityType; // Тип сущности для определения стилей
  type: MarkerType;
  content: string;
  layer?: Layer;
  left?: number;
  top?: number;
  width?: number;
  height?: number;
  originX?: 'left' | 'center' | 'right'; // Точка origin по X
  originY?: 'top' | 'center' | 'bottom'; // Точка origin по Y
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  opacity?: number;
  visible?: boolean;
  // Настройки взаимодействия с пользователем
  selectable?: boolean;
  evented?: boolean;
  hasControls?: boolean;
  hasBorders?: boolean;
  lockMovementX?: boolean;
  lockMovementY?: boolean;
  lockRotation?: boolean;
  lockScalingX?: boolean;
  lockScalingY?: boolean;
  isMarker?: boolean; // Флаг что это маркер
}

// Конфигурация для digital-accent
export interface DigitalAccentConfig {
  digit: TextEntityStyleConfig & {
    spacingTop?: number;
    spacingBottom?: number;
  };
  superscript: {
    fontSize: number; // относительно digit fontSize (например, 0.6)
    offsetX: number; // отступ слева от digit
    offsetY: number | 'top' | 'center' | 'bottom' | 'centered-by-first-line'; // выравнивание по вертикали относительно digit
    fontWeight?: string | number;
    fontFamily?: string;
    fill?: string;
    lineHeight?: number;
  };
}

// Конфигурация стилей для всех типов (включая маркеры)
export interface MetaBulletStylesConfig {
  title: TextEntityStyleConfig & { marker?: MarkerConfig | MarkerLayoutConfig };
  subtitle: TextEntityStyleConfig & { marker?: MarkerConfig | MarkerLayoutConfig };
  'heading-1': TextEntityStyleConfig & { marker?: MarkerConfig | MarkerLayoutConfig };
  'heading-2': TextEntityStyleConfig & { marker?: MarkerConfig | MarkerLayoutConfig };
  'heading-3': TextEntityStyleConfig & { marker?: MarkerConfig | MarkerLayoutConfig };
  'bullet-level-1': TextEntityStyleConfig & { marker?: MarkerConfig | MarkerLayoutConfig };
  'bullet-level-2': TextEntityStyleConfig & { marker?: MarkerConfig | MarkerLayoutConfig };
  'bullet-level-3': TextEntityStyleConfig & { marker?: MarkerConfig | MarkerLayoutConfig };
  'bullet-level-4': TextEntityStyleConfig & { marker?: MarkerConfig | MarkerLayoutConfig };
  'digital-accent': DigitalAccentConfig;
  'digital-accent-superscript': TextEntityStyleConfig; // Используется только для fallback
}

// === СОБЫТИЯ МЕТАБУЛЛИТОВ ===

// События метабуллитов
export interface MetaBulletEvent {
  entityId: string;
  metaBulletId: string;
  eventType: 'text_changed' | 'type_changed' | 'level_changed' | 'position_changed';
  data?: unknown;
}

// === УТИЛИТАРНЫЕ ТИПЫ ===

// Утилитарные типы для работы с буллитами
export type BulletType = Extract<TextEntityType, `bullet-level-${number}`>;
export type NonBulletType = Exclude<TextEntityType, BulletType>;

// === КОНСТАНТЫ И МАППИНГИ ===

// Маппинг shortcuts к типам сущностей для slash-команд
export const ENTITY_TYPE_SHORTCUTS: Record<string, TextEntityType> = {
  // Основные типы
  p: 'bullet-level-1',
  paragraph: 'bullet-level-1',

  // Заголовки
  title: 'title',
  t: 'title',

  subtitle: 'subtitle',
  sub: 'subtitle',

  h1: 'heading-1',
  heading1: 'heading-1',
  '#': 'heading-1',

  h2: 'heading-2',
  heading2: 'heading-2',
  '##': 'heading-2',

  h3: 'heading-3',
  heading3: 'heading-3',
  '###': 'heading-3',

  // Буллиты (минимальный уровень = 1)
  bullet: 'bullet-level-1',
  bullet1: 'bullet-level-1',
  b1: 'bullet-level-1',

  bullet2: 'bullet-level-2',
  b2: 'bullet-level-2',

  bullet3: 'bullet-level-3',
  b3: 'bullet-level-3',

  bullet4: 'bullet-level-4',
  b4: 'bullet-level-4',

  // Digital-accent
  'digital-accent': 'digital-accent',
  da: 'digital-accent',
  digit: 'digital-accent',

  'digital-accent-superscript': 'digital-accent-superscript',
  das: 'digital-accent-superscript',
  superscript: 'digital-accent-superscript',
};

// Обратная мапка - от типа к основному shortcut
export const TYPE_TO_SHORTCUT: Record<TextEntityType, string> = {
  title: 'title',
  subtitle: 'sub',
  'heading-1': 'h1',
  'heading-2': 'h2',
  'heading-3': 'h3',
  'bullet-level-1': 'bullet',
  'bullet-level-2': 'bullet2',
  'bullet-level-3': 'bullet3',
  'bullet-level-4': 'bullet4',
  'digital-accent': 'da',
  'digital-accent-superscript': 'das',
};

// Клавиатурные команды для буллитов (отдельно от slash-команд)
export const BULLET_KEYBOARD_COMMANDS = {
  TAB: 'increase_bullet_level', // Tab - увеличить уровень буллита
  SHIFT_TAB: 'decrease_bullet_level', // Shift+Tab - уменьшить уровень буллита
  ENTER: 'create_new_bullet', // Enter - создать новый буллит того же уровня
} as const;

// === УТИЛИТАРНЫЕ ФУНКЦИИ ===

// Утилитарные функции для работы с типами
export function isBulletType(type: TextEntityType): type is BulletType {
  return type.startsWith('bullet-level-');
}

export function isHeadingType(type: TextEntityType): boolean {
  return type.startsWith('heading-');
}

export function isDigitalAccentType(type: TextEntityType): boolean {
  return type === 'digital-accent' || type === 'digital-accent-superscript';
}

export function isDigitalAccentDigit(type: TextEntityType): boolean {
  return type === 'digital-accent';
}

export function isDigitalAccentSuperscript(type: TextEntityType): boolean {
  return type === 'digital-accent-superscript';
}

export function getBulletLevel(type: BulletType): number {
  const match = type.match(/bullet-level-(\d+)/);
  return match ? parseInt(match[1], 10) : 1;
}

export function getBulletTypeForLevel(level: number): BulletType {
  return `bullet-level-${Math.max(1, Math.min(4, level))}` as BulletType;
}

// Утилитарные функции для работы с shortcuts
export function getEntityTypeByShortcut(shortcut: string): TextEntityType | null {
  const normalizedShortcut = shortcut.toLowerCase().trim();
  return ENTITY_TYPE_SHORTCUTS[normalizedShortcut] || null;
}

export function getShortcutByEntityType(entityType: TextEntityType): string {
  return TYPE_TO_SHORTCUT[entityType] || '';
}

// Получить все доступные shortcuts для типа
export function getAllShortcutsForType(entityType: TextEntityType): string[] {
  return Object.entries(ENTITY_TYPE_SHORTCUTS)
    .filter(([, type]) => type === entityType)
    .map(([shortcut]) => shortcut);
}

// Поиск частичного совпадения для автокомплита
export function findShortcutMatches(
  input: string,
): Array<{ shortcut: string; entityType: TextEntityType }> {
  const normalizedInput = input.toLowerCase().trim();

  return Object.entries(ENTITY_TYPE_SHORTCUTS)
    .filter(([shortcut]) => shortcut.startsWith(normalizedInput))
    .map(([shortcut, entityType]) => ({ shortcut, entityType }))
    .sort((a, b) => a.shortcut.length - b.shortcut.length); // Сначала более короткие
}
